import React from 'react';
import {
  AddConsultantButton,
  AddIcon,
  BackFullIcon,
  CardsIcon,
  ClientsContainer,
  Container,
  Content,
  Header,
  HeaderActions,
  HeaderBack,
  HeaderInfo,
  HeaderTitle,
  KamBamTitle,
  KanbanIcon,
  LoadRow,
  ModalSearch,
  NextPageButton,
  PaginationArea,
  PreviousPageButton,
  Responsive,
  SearchOption,
  SwitchContainer,
  SwitchLabel,
  SwitchThumb,
  SwitchTrack,
} from './styles';

import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { PersonalClientCRUD } from 'services/requests/personal-client';
import PersonalClientCRUDProps, { PersonalClientData } from 'services/requests/personal-client/index.d';

import '@caldwell619/react-kanban/dist/styles.css';
import Load from 'components/load';
import Search from 'components/search';
import MenuBar from 'components/menu-bar';
import styled, { useTheme } from 'styled-components';
import PDFmodal from 'components/modais/pdf';
import CardUserJob from 'components/cards/user-job';
import ManagePdfModal from 'components/modais/manage-pdf';
import PersonalClientModal from 'components/modais/personal-client';
import KanBan from './kanban';
import { KANBAN_BOARD_TEMPLATE, CONST_PERSONAL_CLIENT_KAMBAM_STEP } from 'utils/CONSTANTS';

import typ from './index.d'
import { ColumnMeta } from './index.d'

const KANBAN_PER_PAGE = 20;

const personalClientCrud = new PersonalClientCRUD();

const RecolocationPage: React.FC = () => {
  const { colors } = useTheme();
  const navigation = useNavigate();

  const currentPage = React.useRef(1);
  const isApiRunning = React.useRef(false);
  const columnMetaRef = React.useRef<Map<number, ColumnMeta>>(new Map());

  const [search, setSearch] = React.useState('');
  const [loadingSearch, setLoadingSearch] = React.useState(false);
  const [searchResults, setSearchResults] = React.useState<PersonalClientData[]>();
  const [loading, setLoading] = React.useState(false);
  const [currentClient, setCurrentClient] = React.useState<PersonalClientData>();
  const [currentPdf, setCurrentPdf] = React.useState<number | undefined>();
  const [showModal, setShowModal] = React.useState(false);
  const [showPDF, setPDFModal] = React.useState(false);
  const [showManagePDF, setManagePDF] = React.useState(false);
  const [isKanban, setIsKanban] = React.useState(false)
  const [showLegacy, setShowLegacy] = React.useState(false)

  const [clients, setClients] = React.useState<PersonalClientCRUDProps.readReturn>();
  const [controlledBoard, setBoard] = React.useState<typ.Kanban>({
    columns: [],
  });
  const [columnMeta, setColumnMeta] = React.useState<Map<number, ColumnMeta>>(new Map());

  const getKanbanData = React.useCallback(async () => {
    if (isApiRunning.current) return;

    try {
      isApiRunning.current = true;
      setLoading(true);

      const promises = CONST_PERSONAL_CLIENT_KAMBAM_STEP.map((step, index) =>
        personalClientCrud.read({
          page: 1,
          per_page: KANBAN_PER_PAGE,
          kanban_step: step.keys,
          exclude_origin: showLegacy ? undefined : 'LEGACY',
        }).then(result => ({ index, step, result }))
      );

      const results = await Promise.allSettled(promises);

      let currentData = JSON.parse(JSON.stringify(KANBAN_BOARD_TEMPLATE));
      const newMeta = new Map<number, ColumnMeta>();

      results.forEach(settled => {
        if (settled.status === 'fulfilled') {
          const { index, result } = settled.value;
          const { data, status } = result;

          if (status === 200 && data) {
            currentData.columns[index].cards = data.PersonalClient;
            newMeta.set(index, {
              hasMore: data.has_next,
              currentPage: 1,
              total: data.total,
            });
          } else {
            newMeta.set(index, { hasMore: false, currentPage: 1, total: 0 });
          }
        } else {
          // On failure, set empty meta for the column
          const index = CONST_PERSONAL_CLIENT_KAMBAM_STEP.findIndex(
            s => s === (settled as any).value?.step
          );
          if (index >= 0) {
            newMeta.set(index, { hasMore: false, currentPage: 1, total: 0 });
          }
        }
      });

      columnMetaRef.current = newMeta;
      setColumnMeta(new Map(newMeta));
      setBoard(currentData);
    } catch (e) {
      alert(e);
    } finally {
      isApiRunning.current = false;
      setLoading(false);
    }
  }, [showLegacy]);

  const loadMoreForColumn = React.useCallback(async (columnIndex: number) => {
    const meta = columnMetaRef.current.get(columnIndex);
    if (!meta || !meta.hasMore) return;

    const step = CONST_PERSONAL_CLIENT_KAMBAM_STEP[columnIndex];
    if (!step) return;

    const nextPage = meta.currentPage + 1;

    try {
      const { data, status, error } = await personalClientCrud.read({
        page: nextPage,
        per_page: KANBAN_PER_PAGE,
        kanban_step: step.keys,
        exclude_origin: showLegacy ? undefined : 'LEGACY',
      });

      if (error) throw new Error(error.message.description);

      if (status === 200 && data) {
        setBoard(prev => {
          const newBoard = JSON.parse(JSON.stringify(prev));
          newBoard.columns[columnIndex].cards = [
            ...prev.columns[columnIndex].cards,
            ...data.PersonalClient,
          ];
          return newBoard;
        });

        const updatedMeta: ColumnMeta = {
          hasMore: data.has_next,
          currentPage: nextPage,
          total: data.total,
        };
        columnMetaRef.current.set(columnIndex, updatedMeta);
        setColumnMeta(new Map(columnMetaRef.current));
      }
    } catch (e) {
      toast.error('Erro ao carregar mais clientes.');
    }
  }, [showLegacy]);

  const getClients = React.useCallback(async (page?: number) => {
    if (isApiRunning.current) return;

    try {
      isApiRunning.current = true;
      setLoading(true);

      currentPage.current = page || 1;

      const { data, status, error } = await personalClientCrud.read({
        page: currentPage.current,
        per_page: 16,
        exclude_origin: showLegacy ? undefined : 'LEGACY',
      });

      if (error) {
        throw new Error(error.message.description);
      }

      if (status === 200 && data) {
        setClients(data);
      }
    } catch (e) {
      alert(e);
    } finally {
      isApiRunning.current = false;
      setLoading(false);
    }
  }, [showLegacy]);

  const getClientsBySearch = React.useCallback(async (name: string) => {
    if (loadingSearch) return;
    try {
      setLoadingSearch(true);
      const { data, status, error } = await personalClientCrud.read({
        name
      });

      if (error) {
        throw new Error(error.message.description);
      }


      if (status === 200 && data) {
        setSearchResults(data.PersonalClient)
      }
    } catch (e) {
      alert(e);
    } finally {
      setLoadingSearch(false);
    }
  }, []);

  const handleVisualizatePdf = React.useCallback(async ({id}: {id: number}) => {
    try {
     if(!id) throw new Error('Não foi possivel selecionar o currículo');
      setCurrentPdf(id)
      setPDFModal(true)
    } catch (e) {
      const { message } = e as Error;
      toast.error(message)
    } finally {
      isApiRunning.current = false;
      setLoading(false);
    }
  }, []);



  React.useEffect(() => {
    if (isKanban) {
      getKanbanData();
    } else {
      getClients();
    }
  }, [showLegacy]);

  React.useEffect(() => {
    if(!search) return;
    getClientsBySearch(search)
  }, [search])


  return (
    <Container>
      <MenuBar currentPage="Recolocação" />
      <Content>
        <Header>
          <HeaderBack href="/">
            <BackFullIcon />
          </HeaderBack>
          <HeaderInfo>
            <HeaderTitle>Recolocação profissional</HeaderTitle>

            <HeaderActions>
              <CardsIcon $isActive={!isKanban} onClick={() => {
                if(!isKanban) return;

                getClients();
                setIsKanban(false)
              }}/>
              <KanbanIcon $isActive={isKanban} onClick={() => {
                if(isKanban) return;

                getKanbanData()
                setIsKanban(true)
              }}/>
              {search && (
                <ModalSearch>
                  {searchResults?.length === 0
                    ?
                      <SearchOption>Nenhum cliente encontrado...</SearchOption>
                    :
                      <>
                        {searchResults?.map((e, i) => (
                          <SearchOption key={i} onClick={() => {
                            setSearch('')
                            setSearchResults(undefined)
                            setCurrentClient(e)
                            setShowModal(true)
                          }}>{e.name}</SearchOption>
                        ))}
                      </>
                  }
                </ModalSearch>
              )}
              <Search
                value={search}
                setValue={setSearch}
                placeholder="Pesquisar clientes"
                isLoading={loadingSearch}
              />

              <SwitchContainer>
                <SwitchLabel>Migrados</SwitchLabel>
                <SwitchTrack $active={showLegacy} onClick={() => setShowLegacy(prev => !prev)}>
                  <SwitchThumb $active={showLegacy} />
                </SwitchTrack>
              </SwitchContainer>

              <AddConsultantButton
                title="Adicionar cliente"
                iconRight={AddIcon}
                callback={() => navigation('/criar-cliente-recolocacao')}
              />
            </HeaderActions>
          </HeaderInfo>
        </Header>

        {loading ? (
          <LoadRow>
            <Load
              customStyle={{
                color: colors.secondaryDark,
              }}
              size="extraLarge"
            />
          </LoadRow>
        ) : (
          <>
           {isKanban ? (
            <KanBan
              board={controlledBoard}
              setCurrentClient={setCurrentClient}
              setShowModal={setShowModal}
              loadMoreForColumn={loadMoreForColumn}
              columnMeta={columnMeta}
            />
           ) : (
            <Responsive>
              <ClientsContainer>
                {clients && clients.PersonalClient?.length > 0 && (
                  clients.PersonalClient?.map((e, i) => {
                    return (
                      <CardUserJob
                        key={i}
                        onClick={() => {
                          setCurrentClient(e);
                          setShowModal(true);
                        }}
                        client={{
                          advisor_relation: e.advisor_relation,
                          budget: e.budget,
                          client_type: e.client_type,
                          cpf: e.cpf,
                          id: e.id,
                          image: e.image,
                          kanban_step: e.kanban_step,
                          local: e.local,
                          name: e.name,
                          occupation: e.occupation,
                          status: e.status,
                          vacancy_matching_quantity: e.vacancy_matching_quantity,
                          origin: e.origin,
                        }}
                        haveMargin
                      />
                    );
                  })
                )}
              </ClientsContainer>
              <PaginationArea>
                {clients?.has_prev && (
                  <PreviousPageButton
                    title="Voltar Página"
                    callback={() => getClients(currentPage.current - 1)}
                  />
                )}
                {clients?.has_next && (
                  <NextPageButton
                    title="Proxima Página"
                    callback={() => getClients(currentPage.current + 1)}
                  />
                )}
              </PaginationArea>
            </Responsive>
           )}
          </>
        )}
      </Content>

      {showModal && (
        <PersonalClientModal
          personalClient={currentClient?.id}
          onClose={() => {
            setShowModal(false);
            setCurrentClient(undefined);
          }}
          showPdfCallback={() => setManagePDF(true)}
        />
      )}

      {showPDF && (
        <PDFmodal
          onClose={() => {
            setPDFModal(false);
          }}
          pdfId={currentPdf}
        />
      )}

      {showManagePDF && (
        <ManagePdfModal
          userId={currentClient?.id}
          userName={currentClient?.name}
          type='personal_client'
          onClose={() => {
            setManagePDF(false)
          }}
          onVisualizate={handleVisualizatePdf}
        />
      )}
    </Container>

  );
};

export default RecolocationPage;
